<script setup lang="ts">
import {computed, reactive, ref} from "vue";
import User from "@/Models/User";
import {FilterBar, PageTitle} from "@/Components/Global";
import {Button, Drawer, InputText, Skeleton} from "@/Components/Library";
import ConversationFormDialog from "@/Pages/Conversations/ConversationFormDialog.vue";
import ConversationItem from "@/Pages/Conversations/ConversationItem.vue";
import {getConversations as getConversationsFromApi} from "@/Services/conversations";
import useEcho from "@/Composables/echo";
import {useRouter} from "vue-router";
import {watchDebounced} from "@vueuse/core";
import PageContainer from "@/Components/Global/PageContainer.vue";
import {useFilters} from "@/Composables/filters";
import ConversationIndexEvents from "./conversation-index-events";
import ConversationsIndexFilters from "./ConversationsIndexFilters.vue";

const $user = new User();
const router = useRouter();

const defaultFilters = {
    with: null as "groups" | "contacts" | null,
    archived: false,
};

const {appliedFilters, filters, filtersActive, filtersDirty, applyFilters, resetFilters} = useFilters(
    defaultFilters,
    () => {
        getConversations();
        state.show.filters = false;
    },
);

const conversationsRef = ref([]);
const albieConversation = {
    id: "ai_help",
    title: "Chat with Albie",
    is_ai_chat: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    unread_message_count: 0,
    pivot: {
        user_id: $user.id,
        conversation_id: "ai_help",
        is_archived: false,
        is_pinned: false,
        last_read_at: null,
        last_read_message_id: null,
    },
    participants: [],
};

const conversations = computed(() => {
    const regularConversations = conversationsRef.value
        .filter(
            (c) =>
                (!c.pivot.is_archived && !appliedFilters.archived) || (appliedFilters.archived && c.pivot.is_archived),
        )
        .sort((a, b) => {
            if (a.pivot.is_pinned !== b.pivot.is_pinned) {
                return b.pivot.is_pinned - a.pivot.is_pinned;
            }

            const aDate = new Date(a.created_at);
            const bDate = new Date(b.created_at);
            return bDate.getTime() - aDate.getTime();
        });

    if (!appliedFilters.archived) {
        return [albieConversation, ...regularConversations];
    }

    return regularConversations;
});

const state = reactive({
    show: {
        conversationForm: false,
        filters: false,
    },
    loading: {
        conversations: true,
    },
    archived: false,
});

const search = ref({
    tags: [],
    participants: [],
    text: "",
    strict: false,
});

const totalFiltersActive = computed(() => {
    let count = 0;
    if (filters.with) {
        count++;
    }

    if (filters.archived) {
        count++;
    }

    return count;
});

const clearFilters = () => {
    resetFilters();
    search.value = {
        tags: [],
        participants: [],
        text: "",
        strict: false,
    };

    applyFilters();
};

const getConversations = async () => {
    try {
        state.loading.conversations = true;
        const data = await getConversationsFromApi({
            search: search.value,
            filters: filters,
        });
        console.log("conversations", data.conversations);
        conversationsRef.value = data.conversations;
    } catch (errors) {
        console.log(errors);
    } finally {
        state.loading.conversations = false;
    }
};
getConversations();

useEcho(ConversationIndexEvents(getConversations, $user));

watchDebounced(() => search.value.text, getConversations, {debounce: 1000});
</script>
<template>
    <PageContainer>
        <PageTitle title="Chats" class="mb-2" :show-back-button="false" />
        <div class="mb-3">
            <InputText type="search" placeholder="Search chats" v-model="search.text" fluid />
        </div>
        <div class="flex items-stretch gap-6">
            <!-- filters -->
            <div class="sticky top-2 hidden h-full w-full max-w-sm flex-none 2xl:block">
                <ConversationsIndexFilters
                    v-model="filters"
                    :total-active-filters="totalFiltersActive"
                    @apply="applyFilters"
                    @clear="clearFilters"
                />
            </div>
            <!-- content -->
            <div class="grow pb-6">
                <!-- Loading skeletons -->
                <div v-if="state.loading.conversations" class="py-1">
                    <div
                        v-for="i in 5"
                        :key="`skeleton-${i}`"
                        class="mb-3 flex items-center gap-3 rounded-lg bg-white p-5 shadow dark:bg-stone-900"
                    >
                        <Skeleton width="1rem" height="1rem" class="shrink-0" />
                        <Skeleton width="100%" height="1.25rem" class="grow" />
                        <Skeleton width="1.75rem" height="1.75rem" class="shrink-0" shape="circle" />
                    </div>
                </div>
                <!-- Chats list -->
                <div v-else class="py-1" v-auto-animate>
                    <template v-for="conversation in conversations" :key="conversation.id">
                        <ConversationItem :conversation="conversation" />
                    </template>
                </div>
                <!-- Empty state -->
                <div v-if="!conversations.length && appliedFilters.archived" class="mt-12 text-center">
                    <h3 class="mb-8 text-xl font-semibold">No archived chats</h3>
                </div>
            </div>
        </div>
        <FilterBar v-model="state.show.filters" :total-active-filters="totalFiltersActive">
            <Button
                :icon="{icon: 'mdi:plus', width: '36'}"
                rounded
                class="pointer-events-auto h-14 w-14 bg-askr-green text-white shadow-stone-600"
                @click="state.show.conversationForm = true"
            />
        </FilterBar>
        <Drawer
            v-model:visible="state.show.filters"
            position="bottom"
            header="Filters"
            class="max-h-dvh max-w-3xl overflow-y-auto"
        >
            <div class="pb-12">
                <ConversationsIndexFilters
                    v-model="filters"
                    :total-active-filters="totalFiltersActive"
                    @apply="applyFilters"
                    @clear="clearFilters"
                />
            </div>
        </Drawer>
        <ConversationFormDialog v-model="state.show.conversationForm" />
    </PageContainer>
</template>
